import { useEffect, useState } from 'react';
import type { WeddingEvent } from '@/lib/types';

const STORAGE_KEY = 'wedtrack:drifts';
const MAX_DRIFTS = 3;
const BEHIND_THRESHOLD_MINUTES = 5;

function getDrifts(): number[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
}

function saveDrifts(drifts: number[]): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(drifts.slice(-MAX_DRIFTS)));
}

interface RunningBehindResult {
  isBehind: boolean;
  driftMinutes: number;
}

export function useRunningBehind(events: WeddingEvent[]): RunningBehindResult {
  const [result, setResult] = useState<RunningBehindResult>({ isBehind: false, driftMinutes: 0 });

  useEffect(() => {
    const now = Date.now();
    const drifts = getDrifts();
    let updated = false;

    for (const event of events) {
      if (event.status === 'completed') {
        const scheduledEnd = new Date(event.endTime).getTime();
        if (scheduledEnd < now) {
          const driftMinutes = (now - scheduledEnd) / 60000;
          // Only record if drift is between 0 and 120 min (avoid false positives)
          if (driftMinutes > 0 && driftMinutes < 120) {
            const key = `wedtrack:drift:${event.id}`;
            if (!sessionStorage.getItem(key)) {
              sessionStorage.setItem(key, '1');
              drifts.push(driftMinutes);
              updated = true;
            }
          }
        }
      }
    }

    if (updated) {
      saveDrifts(drifts);
    }

    const recent = getDrifts();
    if (recent.length === 0) {
      setResult({ isBehind: false, driftMinutes: 0 });
      return;
    }

    const avg = recent.reduce((a, b) => a + b, 0) / recent.length;
    setResult({
      isBehind: avg > BEHIND_THRESHOLD_MINUTES,
      driftMinutes: Math.round(avg),
    });
  }, [events]);

  return result;
}
